import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import apiFetch from "../../../../shared/services/apiClient";
import invoiceStyles from "../../invoices/styles/invoice.module.css";
import {
  buildChildrenOf, buildHidden, toggle,
  FiltrosBarra, CardsResumen, TableHeader, FilaCargando, FilaVacia, FilaArbol,
} from "./BalanceComprobacion";
import { usePDFIndividual } from "./EstadoFinanciero";

const fmt = (n) =>
  Math.abs(Number(n ?? 0)).toLocaleString("es-CR", { minimumFractionDigits: 2 });

const TIPO_COLOR_NIVEL1 = { INGRESO: "#4ade80", GASTO: "#f87171" };
const TIPO_COLOR_NIVEL2 = { INGRESO: "#4ade80", GASTO: "#f87171" };

function colorFila(fila) {
  if (fila.nivel === 1) return TIPO_COLOR_NIVEL1[fila.tipo] ?? "#f97316";
  if (fila.nivel === 2) return TIPO_COLOR_NIVEL2[fila.tipo] ?? "#e5ecf5";
  return fila.nivel === 3 ? "#d1d5db" : "#9ca3af";
}

function valorFila(fila) {
  const s = Number(fila.saldo ?? 0);
  if (Math.abs(s) < 0.01) return { text: "—", color: "#374151" };
  const color = fila.tipo === "INGRESO" ? "#4ade80" : "#f87171";
  return { text: `₡${fmt(s)}`, color: fila.nivel <= 2 ? color : "#d1d5db" };
}

// Separador entre secciones
function Separador({ label, color }) {
  return (
    <tr>
      <td colSpan={3} style={{ padding:"7px 14px",
        background: color + "22",
        borderLeft: `3px solid ${color}`,
        color, fontSize:"0.78rem", fontWeight:700,
        textTransform:"uppercase", letterSpacing:"0.8px" }}>
        {label}
      </td>
    </tr>
  );
}

// Fila de total de sección
function FilaTotal({ label, monto, color }) {
  return (
    <tr style={{ borderTop:`1px solid ${color}40` }}>
      <td colSpan={2} style={{ padding:"8px 12px", paddingLeft:26,
        fontWeight:700, color, fontSize:"0.85rem" }}>{label}</td>
      <td className="text-end" style={{ padding:"8px 14px",
        fontFamily:"monospace", fontWeight:800, color, fontSize:"0.88rem" }}>
        ₡{fmt(monto)}
      </td>
    </tr>
  );
}

export default function EstadoResultados() {
  const now       = new Date();
  const mesInicio = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}-01`;
  const mesFin    = new Date(now.getFullYear(), now.getMonth()+1, 0).toISOString().split("T")[0];

  const [fechaDesde,    setFechaDesde]    = useState(mesInicio);
  const [fechaHasta,    setFechaHasta]    = useState(mesFin);
  const [data,          setData]          = useState(null);
  const [loading,       setLoading]       = useState(false);
  const [soloConMov,    setSoloConMov]    = useState(true);
  const [collapsed,     setCollapsed]     = useState(new Set());
  const pdfER = usePDFIndividual('/api/finance/reportes/estado-resultados-pdf/', 'estado_resultados.pdf');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (fechaDesde) qs.set("fecha_desde", fechaDesde);
      if (fechaHasta) qs.set("fecha_hasta", fechaHasta);
      const res = await apiFetch(`/api/finance/reportes/estado-resultados/?${qs}`);
      setData(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [fechaDesde, fechaHasta]);

  useEffect(() => { fetchData(); }, [fetchData]);


  const filas      = data?.filas ?? [];
  const childrenOf = buildChildrenOf(filas);
  const hidden     = buildHidden(filas, childrenOf, collapsed);

  const tieneMovArbol = (id) => {
    const f = filas.find(x => x.id === id);
    if (!f) return false;
    if (f.tiene_movimientos) return true;
    return (childrenOf[id] ?? []).some(tieneMovArbol);
  };

  const ingresos = filas.filter(f => f.tipo === "INGRESO" && !hidden.has(f.id) && (!soloConMov || tieneMovArbol(f.id)));
  const gastos   = filas.filter(f => f.tipo === "GASTO"   && !hidden.has(f.id) && (!soloConMov || tieneMovArbol(f.id)));

  const tI  = Number(data?.total_ingresos ?? 0);
  const tG  = Number(data?.total_gastos   ?? 0);
  const res = Number(data?.resultado      ?? 0);
  const sup = data?.es_superavit ?? true;

  return (
    <>
    
              <span className={invoiceStyles.inactive}>
                <Link to="/">
                  <span className={invoiceStyles.inactive}>Inicio /</span>
                </Link>
                  <span className={invoiceStyles.active}> Estado de Resultados</span>
              </span>
              <div>
                <h2 className="title mt-2 mb-2">Estado de Resultados</h2>
                <p className="mb-3" style={{color:"#9ca3af"}}>Ingresos y Gastos del período seleccionado</p>
              </div>
      <FiltrosBarra
        fechaDesde={fechaDesde} setFechaDesde={setFechaDesde}
        fechaHasta={fechaHasta} setFechaHasta={setFechaHasta}
        soloConMov={soloConMov} setSoloConMov={setSoloConMov}
        onActualizar={fetchData} loading={loading}
        extraButtons={
          <>
            <button onClick={() => pdfER.generar(
                `fecha_desde=${fechaDesde}&fecha_hasta=${fechaHasta}`)}
              disabled={pdfER.loading || loading || !data}
              style={{ padding:'7px 14px', background:'rgba(59,130,246,0.15)',
                border:'1px solid rgba(59,130,246,0.5)', borderRadius:6,
                color:'#60a5fa', fontWeight:600, cursor:'pointer', fontSize:'0.85rem',
                opacity:(pdfER.loading||!data)?0.5:1 }}>
              {pdfER.loading
                ? <><i className='fa-solid fa-circle-notch fa-spin'/> Generando…</>
                : <><i className='fa-solid fa-file-pdf' style={{marginRight:5}}/>PDF</>}
            </button>
            {pdfER.modal}
          </>
        }
      />

      {data && (
        <CardsResumen cards={[
          { label:"Total Ingresos",  value:tI,           color:"#4ade80", icon:"fa-arrow-trend-up" },
          { label:"Total Gastos",    value:tG,           color:"#f87171", icon:"fa-arrow-trend-down" },
          { label: sup ? "Superávit del Ejercicio" : "Déficit del Ejercicio",
            value: Math.abs(res), color: sup ? "#f97316" : "#a78bfa",
            icon:  sup ? "fa-circle-check" : "fa-circle-exclamation" },
        ]}/>
      )}

      <div className={invoiceStyles["table-container"]}>
        <TableHeader
          count={ingresos.length + gastos.length} loading={loading}
          onColapsar={() => setCollapsed(new Set(filas.filter(f => f.nivel < 4).map(f => f.id)))}
          onExpandir={() => setCollapsed(new Set())}
        />
        <div className={`${invoiceStyles["table-body"]} ${invoiceStyles["table-responsive"]}`}>
          <table>
            <thead>
              <tr>
                <th style={{ width:130 }}>Código</th>
                <th>Nombre</th>
                <th className="text-end" style={{ width:160 }}>Monto</th>
              </tr>
            </thead>
            <tbody>
              {loading
                ? <FilaCargando colSpan={3} msg="Calculando estado de resultados…"/>
                : (ingresos.length + gastos.length === 0)
                  ? <FilaVacia colSpan={3} soloConMov={soloConMov} onMostrarTodas={() => setSoloConMov(false)}/>
                  : <>
                    {ingresos.length > 0 && <>
                      <Separador label="Ingresos" color="#4ade80"/>
                      {ingresos.map(f => <FilaArbol key={f.id} fila={f} collapsed={collapsed}
                        onToggle={id => setCollapsed(prev => toggle(prev, id))}
                        colorFn={colorFila} valorFn={valorFila}/>)}
                      <FilaTotal label="Total Ingresos" monto={tI} color="#4ade80"/>
                    </>}
                    {gastos.length > 0 && <>
                      <Separador label="Gastos" color="#f87171"/>
                      {gastos.map(f => <FilaArbol key={f.id} fila={f} collapsed={collapsed}
                        onToggle={id => setCollapsed(prev => toggle(prev, id))}
                        colorFn={colorFila} valorFn={valorFila}/>)}
                      <FilaTotal label="Total Gastos" monto={tG} color="#f87171"/>
                    </>}
                    {data && (
                      <tr style={{ background: sup ? "rgba(249,115,22,0.08)" : "rgba(167,139,250,0.08)",
                        borderTop:`3px solid ${sup ? "#f97316" : "#a78bfa"}` }}>
                        <td colSpan={2} style={{ padding:"12px 12px", fontWeight:800, fontSize:"0.92rem",
                          color: sup ? "#f97316" : "#a78bfa" }}>
                          {sup ? "✦ Superávit del Ejercicio" : "▼ Déficit del Ejercicio"}
                        </td>
                        <td className="text-end" style={{ padding:"12px 14px",
                          fontFamily:"monospace", fontWeight:800, fontSize:"1rem",
                          color: sup ? "#f97316" : "#a78bfa" }}>
                          ₡{fmt(Math.abs(res))}
                        </td>
                      </tr>
                    )}
                  </>}
            </tbody>
          </table>
        </div>
      </div>

    </>
  );
}